import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { type FilterDescriptor } from '../../../lib/resource-list/filters.js';
import { amenities, amenityEndpoints } from '../api/endpoints.js';
import {
  AMENITY_CATEGORY_OPTIONS,
  AMENITY_TYPE_OPTIONS,
  amenityRow,
  type AmenityRow,
} from '../api/schemas.js';

const filters: readonly FilterDescriptor[] = [
  {
    id: 'type',
    label: 'Type',
    param: 'type',
    kind: 'select',
    options: AMENITY_TYPE_OPTIONS,
  },
  {
    id: 'category',
    label: 'Category',
    param: 'category',
    kind: 'select',
    options: AMENITY_CATEGORY_OPTIONS,
  },
];

function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const columns: readonly ColumnDescriptor<AmenityRow>[] = [
  {
    id: 'name',
    header: 'Amenity',
    sortable: true,
    alwaysVisible: true,
    fields: ['name', 'code'],
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/amenities/${row.id}`}
        secondary={row.code ?? null}
      />
    ),
  },
  {
    id: 'type',
    header: 'Type',
    sortable: true,
    fields: ['type'],
    cell: (row) => row.type,
  },
  {
    id: 'category',
    header: 'Category',
    sortable: true,
    fields: ['category'],
    cell: (row) => row.category,
  },
  {
    id: 'description',
    header: 'Description',
    fields: ['description'],
    cell: (row) => row.description,
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    fields: ['createdAt'],
    defaultVisible: false,
    cell: (row) => formatDate(row.createdAt),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name'],
    cell: (row: AmenityRow): React.JSX.Element => <AmenityRowActions row={row} />,
  },
];

function AmenityRowActions({ row }: { readonly row: AmenityRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = amenities.useSoftDelete();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/amenities/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/amenities/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.AMENITY_DELETE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete amenity"
        description={`${label} will be retired from the catalogue. Existing assignments and history are retained.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(row.id, {
            onSuccess: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

export function AmenityListPage(): React.JSX.Element {
  return (
    <ResourceListPage
      title="Amenities"
      description="The tenant vocabulary shared by properties and room types."
      path={amenityEndpoints.list}
      resource="amenities"
      schema={amenityRow}
      columns={columns}
      filters={filters}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.AMENITY_READ}
      actions={
        <PermissionGuard permission={PERMISSIONS.AMENITY_CREATE}>
          <Button asChild>
            <Link to="/amenities/new">
              <Plus className="h-4 w-4" aria-hidden="true" />
              New amenity
            </Link>
          </Button>
        </PermissionGuard>
      }
    />
  );
}
